import { useState, useEffect } from 'react';
import { useStore } from '@nanostores/react';
import { chatId } from '~/lib/persistence/useChatHistory';
import { apiGetStaticDeploymentByChatId, type StaticDeployment } from '~/lib/api/deployments';
import { apiGetStaticAnalytics, getCountryName, type AnalyticsSummary } from '~/lib/api/analytics';
import { classNames } from '~/utils/classNames';

type TimeRange = '7d' | '14d' | '30d';

function formatNumber(num: number): string {
  if (num >= 1000000) {
    return `${(num / 1000000).toFixed(1)}M`;
  }

  if (num >= 1000) {
    return `${(num / 1000).toFixed(1)}K`;
  }

  return num.toString();
}

function formatMs(ms: number | null): string {
  if (ms === null) {
    return '-';
  }

  if (ms < 1000) {
    return `${ms}ms`;
  }

  return `${(ms / 1000).toFixed(2)}s`;
}

function StatCard({ label, value, icon, trend }: { label: string; value: string; icon: string; trend?: string }) {
  return (
    <div className="bg-raised rounded-lg p-3 border border-hairline">
      <div className="flex items-center gap-2 mb-1">
        <div className={classNames(icon, 'text-sm text-[#D9F26A]')} />
        <span className="text-xs text-muted">{label}</span>
      </div>
      <div className="flex items-baseline gap-2">
        <span className="text-xl font-semibold text-primary">{value}</span>
        {trend && <span className="text-xs text-green-400">{trend}</span>}
      </div>
    </div>
  );
}

function MiniBarChart({ data, maxValue }: { data: Array<{ date: string; value: number }>; maxValue: number }) {
  return (
    <div className="flex items-end gap-0.5 h-12">
      {data.map((d, i) => (
        <div
          key={i}
          className="flex-1 bg-[#D9F26A]/60 rounded-t hover:bg-[#D9F26A] transition-colors"
          style={{ height: maxValue > 0 ? `${(d.value / maxValue) * 100}%` : '2px' }}
          title={`${d.date}: ${d.value}`}
        />
      ))}
    </div>
  );
}

function ListItem({ rank, label, value, total }: { rank: number; label: string; value: number; total: number }) {
  const pct = total > 0 ? (value / total) * 100 : 0;
  return (
    <div className="flex items-center gap-2 py-1.5">
      <span className="text-xs text-muted w-4">{rank}</span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-0.5">
          <span className="text-xs text-primary truncate">{label}</span>
          <span className="text-xs text-muted ml-2">{value}</span>
        </div>
        <div className="h-1 bg-sunken rounded-full overflow-hidden">
          <div className="h-full bg-[#D9F26A]/60 rounded-full" style={{ width: `${pct}%` }} />
        </div>
      </div>
    </div>
  );
}

export function AnalyticsPanel() {
  const currentChatId = useStore(chatId);
  const [deployment, setDeployment] = useState<StaticDeployment | null>(null);
  const [analytics, setAnalytics] = useState<AnalyticsSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState<TimeRange>('7d');

  useEffect(() => {
    if (!currentChatId) {
      setLoading(false);
      return;
    }

    setLoading(true);

    apiGetStaticDeploymentByChatId(currentChatId)
      .then((dep) => {
        setDeployment(dep);

        if (dep?.id) {
          const days = timeRange === '7d' ? 7 : timeRange === '14d' ? 14 : 30;
          return apiGetStaticAnalytics(dep.id, days);
        }

        return null;
      })
      .then((data) => {
        setAnalytics(data);
      })
      .finally(() => setLoading(false));
  }, [currentChatId, timeRange]);

  if (loading) {
    return (
      <div className="flex flex-col h-full p-4">
        <div className="flex items-center gap-2 mb-4">
          <div className="i-ph:chart-line text-lg text-[#D9F26A]" />
          <h3 className="text-sm font-semibold text-primary">Analytics</h3>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="i-svg-spinners:90-ring-with-bg text-xl text-muted" />
        </div>
      </div>
    );
  }

  if (!deployment) {
    return (
      <div className="flex flex-col h-full p-4">
        <div className="flex items-center gap-2 mb-4">
          <div className="i-ph:chart-line text-lg text-[#D9F26A]" />
          <h3 className="text-sm font-semibold text-primary">Analytics</h3>
        </div>
        <div className="flex-1 flex flex-col items-center justify-center text-center">
          <div className="w-12 h-12 rounded-xl bg-raised flex items-center justify-center mb-4">
            <div className="i-ph:rocket-launch text-2xl text-muted" />
          </div>
          <h4 className="text-sm font-medium text-primary mb-1">Deploy first</h4>
          <p className="text-xs text-muted max-w-[240px] leading-relaxed">
            Deploy your landing page to start collecting analytics.
          </p>
        </div>
      </div>
    );
  }

  const summary = analytics?.summary || {
    pageviews: 0,
    visitors: 0,
    errors: 0,
    avgFcp: null,
    avgLcp: null,
    avgCls: null,
    avgPageLoad: null,
  };
  const daily = analytics?.daily || [];
  const maxPageviews = Math.max(...daily.map((d) => d.pageviews), 1);

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-hairline">
        <div className="flex items-center gap-2">
          <div className="i-ph:chart-line text-lg text-[#D9F26A]" />
          <h3 className="text-sm font-semibold text-primary">Analytics</h3>
        </div>
        <select
          value={timeRange}
          onChange={(e) => setTimeRange(e.target.value as TimeRange)}
          className="text-xs px-2 py-1.5 rounded-md bg-raised border border-hairline text-primary"
        >
          <option value="7d">Last 7 days</option>
          <option value="14d">Last 14 days</option>
          <option value="30d">Last 30 days</option>
        </select>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        {/* Summary Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          <StatCard label="Pageviews" value={formatNumber(summary.pageviews)} icon="i-ph:eye" />
          <StatCard label="Visitors" value={formatNumber(summary.visitors)} icon="i-ph:users" />
          <StatCard label="Errors" value={formatNumber(summary.errors)} icon="i-ph:warning" />
        </div>

        {/* Daily Chart */}
        {daily.length > 0 && (
          <section>
            <h4 className="text-xs font-semibold text-muted mb-2">PAGEVIEWS</h4>
            <div className="bg-raised rounded-lg p-3 border border-hairline">
              <MiniBarChart data={daily.map((d) => ({ date: d.date, value: d.pageviews }))} maxValue={maxPageviews} />
              <div className="flex justify-between mt-1">
                <span className="text-[10px] text-muted">{daily[0]?.date}</span>
                <span className="text-[10px] text-muted">{daily[daily.length - 1]?.date}</span>
              </div>
            </div>
          </section>
        )}

        {/* Top Pages */}
        {analytics?.topPages && analytics.topPages.length > 0 && (
          <section>
            <h4 className="text-xs font-semibold text-muted mb-2">TOP PAGES</h4>
            <div className="bg-raised rounded-lg p-3 border border-hairline">
              {analytics.topPages.slice(0, 5).map((page, i) => (
                <ListItem key={page.path} rank={i + 1} label={page.path} value={page.views} total={summary.pageviews} />
              ))}
            </div>
          </section>
        )}

        {/* Top Countries */}
        {analytics?.topCountries && analytics.topCountries.length > 0 && (
          <section>
            <h4 className="text-xs font-semibold text-muted mb-2">TOP COUNTRIES</h4>
            <div className="bg-raised rounded-lg p-3 border border-hairline">
              {analytics.topCountries.slice(0, 5).map((c, i) => (
                <ListItem
                  key={c.country}
                  rank={i + 1}
                  label={getCountryName(c.country)}
                  value={c.visits}
                  total={summary.pageviews}
                />
              ))}
            </div>
          </section>
        )}

        {/* Top Referrers */}
        {analytics?.topReferrers && analytics.topReferrers.length > 0 && (
          <section>
            <h4 className="text-xs font-semibold text-muted mb-2">TOP REFERRERS</h4>
            <div className="bg-raised rounded-lg p-3 border border-hairline">
              {analytics.topReferrers.slice(0, 5).map((ref, i) => (
                <ListItem
                  key={ref.source}
                  rank={i + 1}
                  label={ref.source}
                  value={ref.visits}
                  total={summary.pageviews}
                />
              ))}
            </div>
          </section>
        )}

        {/* Devices */}
        {analytics?.devices && (
          <section>
            <h4 className="text-xs font-semibold text-muted mb-2">DEVICES</h4>
            <div className="bg-raised rounded-lg p-3 border border-hairline">
              <div className="flex gap-4">
                {[
                  { label: 'Desktop', value: analytics.devices.desktop, icon: 'i-ph:desktop' },
                  { label: 'Mobile', value: analytics.devices.mobile, icon: 'i-ph:device-mobile' },
                  { label: 'Tablet', value: analytics.devices.tablet, icon: 'i-ph:device-tablet' },
                ].map((device) => {
                  const total = analytics.devices.desktop + analytics.devices.mobile + analytics.devices.tablet;
                  const pct = total > 0 ? Math.round((device.value / total) * 100) : 0;

                  return (
                    <div key={device.label} className="flex-1 text-center">
                      <div className={classNames(device.icon, 'text-lg text-muted mx-auto mb-1')} />
                      <div className="text-sm font-medium text-primary">{pct}%</div>
                      <div className="text-[10px] text-muted">{device.label}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        {/* Performance */}
        <section>
          <h4 className="text-xs font-semibold text-muted mb-2">PERFORMANCE</h4>
          <div className="bg-raised rounded-lg p-3 border border-hairline">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-[10px] text-muted mb-0.5">First Contentful Paint</div>
                <div className="text-sm font-medium text-primary">{formatMs(summary.avgFcp)}</div>
              </div>
              <div>
                <div className="text-[10px] text-muted mb-0.5">Largest Contentful Paint</div>
                <div className="text-sm font-medium text-primary">{formatMs(summary.avgLcp)}</div>
              </div>
              <div>
                <div className="text-[10px] text-muted mb-0.5">Cumulative Layout Shift</div>
                <div className="text-sm font-medium text-primary">{summary.avgCls ?? '-'}</div>
              </div>
              <div>
                <div className="text-[10px] text-muted mb-0.5">Page Load</div>
                <div className="text-sm font-medium text-primary">{formatMs(summary.avgPageLoad)}</div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
